import React from 'react';
import { Trash2 } from 'lucide-react';

const STATUS_LIST = ['en_attente', 'en_cours', 'termine', 'annule'];
const STATUS_LABELS = { en_attente: 'En attente', en_cours: 'En cours', termine: 'Terminé', annule: 'Annulé' };

export default function InterventionForm({
  form, setForm, editing, clients, vehicles, onSave, onClose
}) {
  const clientVehicles = form.client_id ? vehicles.filter(v => v.client_id === form.client_id) : [];

  const addPart = () => setForm({ ...form, parts_used: [...form.parts_used, { name: '', quantity: 1, price: 0 }] });
  const updatePart = (idx, field, value) => {
    const parts = [...form.parts_used];
    parts[idx] = { ...parts[idx], [field]: field === 'name' ? value : parseFloat(value) || 0 };
    setForm({ ...form, parts_used: parts });
  };
  const removePart = (idx) => setForm({ ...form, parts_used: form.parts_used.filter((_, i) => i !== idx) });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-2xl my-8">
        <div className="p-5 border-b flex justify-between items-center">
          <h3 className="font-semibold text-lg">{editing ? "Modifier l'intervention" : 'Nouvelle intervention'}</h3>
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="intervention-status-select">
            {STATUS_LIST.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <form onSubmit={onSave} className="p-5 space-y-4" data-testid="intervention-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select required value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value, vehicle_id: '' })} className="border rounded-lg px-3 py-2 text-sm" data-testid="intervention-client-select">
              <option value="">Client *</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company_name}</option>)}
            </select>
            <select value={form.vehicle_id || ''} onChange={e => setForm({ ...form, vehicle_id: e.target.value })} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">Véhicule (optionnel)</option>
              {clientVehicles.map(v => <option key={v.id} value={v.id}>{v.brand} {v.model} - {v.plate}</option>)}
            </select>
          </div>

          <textarea required placeholder="Description des travaux *" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="intervention-description" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date d'entrée</label>
              <input type="date" value={form.entry_date} onChange={e => setForm({ ...form, entry_date: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date prévue de fin</label>
              <input type="date" value={form.expected_date} onChange={e => setForm({ ...form, expected_date: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Technicien</label>
              <input placeholder="Nom du technicien" value={form.technician} onChange={e => setForm({ ...form, technician: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Temps passé (h)</label>
              <input type="number" step="0.5" min="0" value={form.time_spent} onChange={e => setForm({ ...form, time_spent: parseFloat(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Montant estimé</label>
              <input type="number" step="0.01" min="0" value={form.estimated_amount} onChange={e => setForm({ ...form, estimated_amount: parseFloat(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Montant final</label>
              <input type="number" step="0.01" min="0" value={form.final_amount} onChange={e => setForm({ ...form, final_amount: parseFloat(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-semibold">Pièces utilisées</h4>
              <button type="button" onClick={addPart} className="text-xs text-[#DC2626] hover:underline">+ Ajouter une pièce</button>
            </div>
            {form.parts_used.map((part, idx) => (
              <div key={`part-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-center">
                <input placeholder="Nom pièce" value={part.name} onChange={e => updatePart(idx, 'name', e.target.value)} className="col-span-5 border rounded px-2 py-1.5 text-xs" />
                <input type="number" min="1" placeholder="Qté" value={part.quantity} onChange={e => updatePart(idx, 'quantity', e.target.value)} className="col-span-2 border rounded px-2 py-1.5 text-xs text-center" />
                <input type="number" step="0.01" placeholder="Prix" value={part.price} onChange={e => updatePart(idx, 'price', e.target.value)} className="col-span-3 border rounded px-2 py-1.5 text-xs text-right" />
                <button type="button" onClick={() => removePart(idx)} className="col-span-2 text-red-400 hover:text-red-600 text-center"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>

          <textarea placeholder="Notes internes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />

          <div className="flex justify-end gap-3 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button type="submit" className="px-4 py-2 text-sm bg-[#DC2626] text-white rounded-lg hover:bg-red-700" data-testid="save-intervention-btn">{editing ? 'Enregistrer' : "Créer l'intervention"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
